import {
  Download,
  Phone,
  Mail,
  UserPlus,
  Ticket,
  Plane,
  Clock,
  Calendar,
  Luggage,
  CheckCircle2,
  MapPin,
} from "lucide-react";
import Seo from "@/components/Seo";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const PDF_URL = `${API_BASE}/api/assets/london-harare-flyer.pdf`;
const VCARD_URL = `${API_BASE}/api/assets/simba-travel.vcf`;
const SALES_TEL = "+442030237701";
const SALES_EMAIL = "Sales@simbatravel.co.uk";

const SCHEDULE = [
  { day: "Wednesday", out: "UM 707 · LGW 18:00 → HRE 05:30 (+1)", ret: "UM 708 · HRE 09:30 → LGW 17:00" },
  { day: "Friday",    out: "UM 707 · LGW 18:00 → HRE 05:30 (+1)", ret: "UM 708 · HRE 09:30 → LGW 17:00" },
  { day: "Sunday",    out: "UM 707 · LGW 18:00 → HRE 05:30 (+1)", ret: "UM 708 · HRE 09:30 → LGW 17:00" },
];

export default function OfferLondonHararePage() {
  return (
    <div data-testid="offer-lgw-hre-page" className="bg-linen">
      <Seo
        title="Time Saver Promo — London Gatwick to Harare from £515 | Air Zimbabwe · Simba Travel"
        description="Non-stop Air Zimbabwe flights London Gatwick to Harare. Economy Time Saver Promo from £515 one-way / £700 return. 3× weekly on A330-200. Book with Simba Travel, the UK GSA."
      />

      {/* =================== FLYER =================== */}
      <section className="pt-20 md:pt-24 pb-6 md:pb-10" data-testid="offer-flyer">
        <div className="container-edge">
          <article
            className="max-w-4xl mx-auto bg-linen border border-ink/10 shadow-[0_20px_60px_-20px_rgba(19,26,34,0.25)] overflow-hidden"
            data-testid="flyer-card"
          >
            {/* Flyer header — no logo (already in site header above); keeps the promo badge */}
            <header className="bg-linen border-b border-ink/10 relative">
              {/* Terracotta accent stripe */}
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-terracotta via-ochre to-terracotta" aria-hidden="true" />
              <div className="relative px-6 md:px-10 py-4 md:py-5 flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-sm tracking-[0.2em] uppercase text-ink font-medium">Simba Travel</div>
                  <div className="text-[11px] tracking-wide text-terracotta mt-1">Air Zimbabwe GSA for the UK &amp; Ireland</div>
                </div>
                <div className="flex items-center gap-2 border border-terracotta/50 bg-terracotta/5 px-3 py-1.5">
                  <Ticket className="w-3.5 h-3.5 stroke-1 text-terracotta" aria-hidden="true" />
                  <span className="text-[10px] tracking-[0.24em] uppercase text-terracotta font-medium">Time Saver Promo</span>
                </div>
              </div>
            </header>

            {/* Flyer hero */}
            <div className="px-6 md:px-10 pt-10 md:pt-12 pb-8 md:pb-10 border-b border-ink/10">
              <div className="overline text-terracotta mb-4">Non-stop from the UK</div>
              <h1
                className="font-serif text-4xl md:text-5xl lg:text-6xl text-ink leading-[1.05] tracking-tight"
                data-testid="flyer-title"
              >
                London Gatwick <span className="text-terracotta">→</span> Harare
              </h1>
              <p className="mt-5 text-base md:text-lg text-ink/70 max-w-2xl font-light leading-relaxed">
                Fly non-stop with Air Zimbabwe. Three departures a week on the Airbus A330-200 —
                Zimbabwean hospitality in the skies, straight from the heart of London.
              </p>

              {/* Route quick-facts */}
              <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                <RouteFact icon={Plane} label="Aircraft" value="Airbus A330-200" />
                <RouteFact icon={Clock} label="Flight time" value="≈ 10h 30m" />
                <RouteFact icon={Calendar} label="Frequency" value="3× weekly" />
                <RouteFact icon={MapPin} label="Cabin classes" value="Economy · Business" />
              </div>
            </div>

            {/* FARES */}
            <div className="px-6 md:px-10 py-10 md:py-14 bg-sand border-b border-ink/10">
              <div className="overline text-terracotta mb-3">Fares</div>
              <p className="text-sm text-ink/70 max-w-2xl">
                Economy Time Saver Promo applies on outbound travel between{" "}
                <strong className="text-ink">15 July and 15 November 2026</strong>. Business Class
                low-season published fares shown.
              </p>

              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* ECONOMY */}
                <div
                  className="relative border border-ink/15 bg-linen p-7 md:p-8"
                  data-testid="flyer-fare-economy"
                >
                  <div className="absolute -top-3 left-6 bg-terracotta text-linen text-[10px] tracking-[0.25em] uppercase px-3 py-1">
                    Best value
                  </div>
                  <div className="overline text-terracotta mt-2">Economy · Time Saver Promo</div>
                  <div className="mt-6 grid grid-cols-2 gap-4 items-end">
                    <div>
                      <div className="text-[10px] tracking-widest uppercase text-muted2">Return fare starting from</div>
                      <div className="font-serif text-4xl md:text-5xl text-ink mt-1 leading-none">£700</div>
                    </div>
                    <div>
                      <div className="text-[10px] tracking-widest uppercase text-muted2">One way starting from</div>
                      <div className="font-serif text-3xl text-ink/80 mt-1 leading-none">£515</div>
                    </div>
                  </div>
                  <ul className="mt-6 space-y-2.5 text-sm">
                    <FareBullet>2 pieces × 23 kg checked baggage</FareBullet>
                    <FareBullet>1 × 10 kg cabin baggage</FareBullet>
                    <FareBullet>Limited promotional availability</FareBullet>
                    <FareBullet>Family &amp; group quotes on request</FareBullet>
                  </ul>
                </div>

                {/* BUSINESS */}
                <div
                  className="border-2 border-terracotta bg-linen p-7 md:p-8"
                  data-testid="flyer-fare-business"
                >
                  <div className="overline text-terracotta">Business Class</div>
                  <div className="mt-6 grid grid-cols-2 gap-4 items-end">
                    <div>
                      <div className="text-[10px] tracking-widest uppercase text-muted2">Return fare starting from</div>
                      <div className="font-serif text-4xl md:text-5xl text-ink mt-1 leading-none">£2,260</div>
                    </div>
                    <div>
                      <div className="text-[10px] tracking-widest uppercase text-muted2">One way starting from</div>
                      <div className="font-serif text-3xl text-ink/80 mt-1 leading-none">£1,430</div>
                    </div>
                  </div>
                  <ul className="mt-6 space-y-2.5 text-sm">
                    <FareBullet>3 × 32 kg checked baggage</FareBullet>
                    <FareBullet>1 × 10 kg cabin baggage</FareBullet>
                    <FareBullet>Free date changes &amp; seat selection</FareBullet>
                    <FareBullet>Priority check-in &amp; lounge access</FareBullet>
                  </ul>
                </div>
              </div>

              {/* Inline Call to Book — right after the price cards for instant conversion */}
              <a
                href={`tel:${SALES_TEL}`}
                className="mt-8 w-full flex items-center justify-center gap-3 bg-terracotta text-linen border border-terracotta px-6 py-5 hover:bg-terracotta/90 transition-colors group"
                data-testid="fares-inline-call"
              >
                <Phone className="w-5 h-5 stroke-1" aria-hidden="true" />
                <span className="font-serif text-lg md:text-xl tracking-tight">
                  Call to Book · +44 20 3023 7701
                </span>
              </a>

              <p className="mt-6 text-[11px] text-muted2 italic max-w-3xl leading-relaxed">
                All prices in GBP per person, including taxes and carrier charges. Subject to
                availability at time of booking. High-season Business fares on request.
              </p>
            </div>

            {/* SCHEDULE */}
            <div className="px-6 md:px-10 py-10 md:py-14 border-b border-ink/10">
              <div className="overline text-terracotta mb-3">Weekly schedule</div>
              <h2 className="editorial-h2 text-ink">Three departures a week from Gatwick.</h2>
              <p className="mt-3 text-sm text-ink/70">
                Schedule valid <strong>22 July 2026 – 27 March 2027</strong>. All times local.
              </p>

              <div className="mt-8 border border-ink/10 overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]" data-testid="flyer-schedule-table">
                  <thead className="bg-sand border-b-2 border-terracotta">
                    <tr>
                      <th className="text-left px-5 py-3 font-medium text-[10px] tracking-[0.22em] uppercase text-terracotta">Day</th>
                      <th className="text-left px-5 py-3 font-medium text-[10px] tracking-[0.22em] uppercase text-terracotta">Outbound (LGW → HRE)</th>
                      <th className="text-left px-5 py-3 font-medium text-[10px] tracking-[0.22em] uppercase text-terracotta">Return (HRE → LGW)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {SCHEDULE.map((s, i) => (
                      <tr
                        key={s.day}
                        className={i % 2 ? "bg-sand/60" : "bg-linen"}
                        data-testid={`flyer-schedule-${s.day.toLowerCase()}`}
                      >
                        <td className="px-5 py-4 font-serif text-lg text-ink">{s.day}</td>
                        <td className="px-5 py-4 font-mono text-xs text-ink/80">{s.out}</td>
                        <td className="px-5 py-4 font-mono text-xs text-ink/80">{s.ret}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* BAGGAGE + BOOK WITH GSA */}
            <div className="px-6 md:px-10 py-10 md:py-14 bg-sand grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {/* Baggage */}
              <div className="border border-ink/10 bg-linen p-7">
                <Luggage className="w-7 h-7 stroke-1 text-terracotta" aria-hidden="true" />
                <h3 className="font-serif text-2xl mt-4 text-ink">Baggage allowance</h3>
                <div className="mt-5 space-y-4 text-sm text-ink/80">
                  <div>
                    <div className="overline text-terracotta">Economy</div>
                    <div className="mt-1">2 pieces × 23 kg checked · 1 × 10 kg cabin</div>
                  </div>
                  <div>
                    <div className="overline text-terracotta">Business</div>
                    <div className="mt-1">3 pieces × 32 kg checked · 1 × 10 kg cabin</div>
                  </div>
                  <p className="text-[11px] text-muted2 italic pt-2 border-t border-ink/10">
                    Excess baggage rates available at the airport or via Simba Travel Ticketing.
                  </p>
                </div>
              </div>

              {/* Book with the UK GSA */}
              <div className="border-2 border-terracotta bg-linen p-7 flex flex-col">
                <div className="overline text-terracotta">Book with the UK GSA</div>
                <h3 className="font-serif text-2xl mt-4 text-ink">Simba Travel · London</h3>
                <div className="mt-5 space-y-3 text-sm">
                  <a
                    href={`tel:${SALES_TEL}`}
                    className="flex items-center gap-3 text-ink hover:text-terracotta transition-colors"
                    data-testid="flyer-inline-phone"
                  >
                    <Phone className="w-4 h-4 stroke-1 text-terracotta" />
                    <span className="font-serif text-xl">+44 20 3023 7701</span>
                  </a>
                  <a
                    href={`mailto:${SALES_EMAIL}`}
                    className="flex items-center gap-3 text-ink/80 hover:text-terracotta transition-colors"
                    data-testid="flyer-inline-email"
                  >
                    <Mail className="w-4 h-4 stroke-1 text-terracotta" />
                    <span>{SALES_EMAIL}</span>
                  </a>
                </div>
                <div className="mt-auto pt-6 text-[11px] text-muted2 leading-relaxed">
                  Contact centre 09:00 – 23:00 UK time, seven days a week. Simba Limited
                  (Reg. 09422922) — 13 Hogarth Place, London SW5 0QT.
                </div>
              </div>
            </div>

            {/* Footer strip */}
            <div className="px-6 md:px-10 py-4 bg-sand border-t-2 border-terracotta text-ink/70 flex flex-wrap items-center justify-between gap-2 text-[11px]">
              <span className="tracking-[0.18em] uppercase text-terracotta font-medium">Air Zimbabwe · UK GSA</span>
              <span>simbatravel.co.uk</span>
            </div>
          </article>
        </div>
      </section>

      {/* =================== CTA ROW =================== */}
      <section className="pb-16 md:pb-24" data-testid="offer-ctas">
        <div className="container-edge max-w-4xl mx-auto">
          <div className="overline text-terracotta mb-3 text-center">Next step</div>
          <h2 className="editorial-h2 text-center max-w-2xl mx-auto">Book, save or share this offer.</h2>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <CtaButton
              testId="cta-download-pdf"
              href={PDF_URL}
              download="Simba-Travel-Air-Zimbabwe-Time-Saver-Promo.pdf"
              icon={Download}
              label="Download PDF"
              helper="Save or print"
              variant="light"
            />
            <CtaButton
              testId="cta-call-sales"
              href={`tel:${SALES_TEL}`}
              icon={Phone}
              label="Call to Book"
              helper="+44 20 3023 7701"
              variant="accent"
            />
            <CtaButton
              testId="cta-email-sales"
              href={`mailto:${SALES_EMAIL}?subject=Time%20Saver%20Promo%20%E2%80%94%20LGW%2FHRE%20enquiry`}
              icon={Mail}
              label="Email Us"
              helper="Sales@simbatravel.co.uk"
              variant="light"
            />
            <CtaButton
              testId="cta-save-contact"
              href={VCARD_URL}
              download="Simba-Travel.vcf"
              icon={UserPlus}
              label="Save Contact"
              helper="Add to phone (.vcf)"
              variant="light"
            />
          </div>

          <p className="mt-10 text-[11px] text-muted2 italic text-center max-w-2xl mx-auto leading-relaxed">
            Promotional fares from £515 one-way / £700 return in Economy Time Saver Promo.
            Outbound travel 15 Jul – 15 Nov 2026. Prices in GBP per person, including taxes
            and carrier charges, subject to availability at time of booking. Terms and
            conditions apply.
          </p>
        </div>
      </section>
    </div>
  );
}

/* ---------- Small helpers (inline, no separate files) ---------- */

function RouteFact({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 stroke-1 text-terracotta mt-1 flex-shrink-0" aria-hidden="true" />
      <div>
        <div className="text-[10px] tracking-[0.22em] uppercase text-muted2">{label}</div>
        <div className="text-sm text-ink font-medium mt-0.5">{value}</div>
      </div>
    </div>
  );
}

function FareBullet({ children, dark = false }) {
  return (
    <li className="flex items-start gap-2.5">
      <CheckCircle2
        className={`w-4 h-4 stroke-1 mt-0.5 flex-shrink-0 ${dark ? "text-ochre" : "text-terracotta"}`}
        aria-hidden="true"
      />
      <span className={dark ? "text-linen/85" : "text-ink/80"}>{children}</span>
    </li>
  );
}

function CtaButton({ testId, href, icon: Icon, label, helper, variant, download }) {
  const isAccent = variant === "accent";
  const base =
    "group flex flex-col items-center justify-center text-center px-4 py-6 md:py-7 border transition-all duration-300 hover:-translate-y-0.5";
  const styles = isAccent
    ? "bg-terracotta text-linen border-terracotta hover:bg-terracotta/90"
    : "bg-linen text-ink border-ink/15 hover:border-terracotta hover:bg-sand";
  return (
    <a
      href={href}
      download={download}
      className={`${base} ${styles}`}
      data-testid={testId}
    >
      <Icon
        className={`w-6 h-6 stroke-1 ${isAccent ? "text-linen" : "text-terracotta"}`}
        aria-hidden="true"
      />
      <span className="mt-3 font-serif text-base md:text-lg">{label}</span>
      <span className={`mt-1 text-[10px] tracking-wide ${isAccent ? "text-linen/80" : "text-muted2"}`}>
        {helper}
      </span>
    </a>
  );
}
